import React, { useState, useEffect } from 'react';
import { 
  Trophy, Users, TrendingUp, Target, Activity, 
  BarChart3, Calendar, Star, Crown, Zap,
  ChevronDown, Menu, X, Award, Filter, Search,
  DollarSign, TrendingDown, ChevronUp, Eye,
  User, Clock, MapPin, Layers
} from 'lucide-react';
import DraftTable from './DraftTable';
import InvestmentChart from './InvestmentChart';
import DraftExplorerBanner from './DraftExplorerBanner';

const DraftExplorer = () => {
  // State for filters matching the R Shiny app structure
  const [filters, setFilters] = useState({
    yearRange: [2012, 2024],
    managers: ['Kevin', 'Jake', 'Lyons', 'Nipples', 'Dan', 'Shuds', 'Karp', 'Mills', 'Joey', 'Raffy', 'Oster', 'Scott', 'Sam'],
    investmentTypes: ['Drafted', 'Keeper'],
    positions: ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'],
    costRange: [1, 100]
  });

  const [draftData, setDraftData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableYears, setAvailableYears] = useState({ min: 2012, max: 2024 });
  const [maxCost, setMaxCost] = useState(69);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const managersOptions = ['Kevin', 'Jake', 'Lyons', 'Nipples', 'Dan', 'Shuds', 'Karp', 'Mills', 'Joey', 'Raffy', 'Oster', 'Scott', 'Sam'];
  const investmentTypeOptions = ['Drafted', 'Keeper'];
  const positionOptions = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get filtered data for child components
  const getFilteredData = () => {
    return draftData.filter(record => {
      return (
        record.Year >= filters.yearRange[0] &&
        record.Year <= filters.yearRange[1] &&
        filters.managers.includes(record.Manager) &&
        filters.investmentTypes.includes(record.Investment) &&
        filters.positions.includes(record.Position) &&
        record.Cost >= filters.costRange[0] &&
        record.Cost <= filters.costRange[1]
      );
    });
  };

  const filteredData = getFilteredData();

  // Load and parse Excel data
  useEffect(() => {
    const loadDraftData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if we have the uploaded file available
        if (typeof window !== 'undefined' && window.fs && window.fs.readFile) {
          const arrayBuffer = await window.fs.readFile('BoBData.xlsx');
          
          if (!window.XLSX) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            script.onload = () => {
              parseExcelData(arrayBuffer);
            };
            script.onerror = () => {
              setError('Failed to load XLSX library');
              setLoading(false);
            };
            document.head.appendChild(script);
          } else {
            parseExcelData(arrayBuffer);
          }
        } else {
          // Fallback: try to fetch from public folder
          const response = await fetch('/BoBData.xlsx');
          if (!response.ok) {
            throw new Error('Could not fetch Excel file');
          }
          const arrayBuffer = await response.arrayBuffer();
          
          if (!window.XLSX) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            script.onload = () => {
              parseExcelData(arrayBuffer);
            };
            script.onerror = () => {
              setError('Failed to load XLSX library');
              setLoading(false);
            };
            document.head.appendChild(script);
          } else {
            parseExcelData(arrayBuffer);
          }
        }
      } catch (error) {
        console.error('Error loading draft data:', error);
        setError('Error loading draft data: ' + error.message);
        setLoading(false);
      }
    };

    const parseExcelData = (arrayBuffer) => {
      try {
        const workbook = window.XLSX.read(arrayBuffer, { type: 'array' });
        
        console.log('Available sheets:', workbook.SheetNames);
        console.log('Looking for draft data in sheet 25 (dd dataframe)');
        
        // Read draft data from sheet 25 (dd in R code) - this should be the 25th sheet (index 24)
        let draftSheet = null;
        let sheetName = '';
        
        // Try to find the correct sheet - could be at index 24 or named something specific
        if (workbook.SheetNames.length >= 25) {
          sheetName = workbook.SheetNames[24]; // 0-indexed, so sheet 25 is index 24
          draftSheet = workbook.Sheets[sheetName];
          console.log('Found sheet at index 24:', sheetName);
        }
        
        // If that doesn't work, look for a sheet with draft-related data
        if (!draftSheet) {
          const draftSheetNames = workbook.SheetNames.filter(name => 
            name.toLowerCase().includes('draft') || 
            name.toLowerCase().includes('dd') ||
            name.toLowerCase().includes('player')
          );
          
          if (draftSheetNames.length > 0) {
            sheetName = draftSheetNames[0];
            draftSheet = workbook.Sheets[sheetName];
            console.log('Found draft sheet by name:', sheetName);
          }
        }
        
        if (!draftSheet) {
          throw new Error('Draft data sheet not found. Expected sheet 25 with columns: Year, Owner, Player, Position, Cost, Keeper.Status');
        }
        
        const jsonData = window.XLSX.utils.sheet_to_json(draftSheet);
        console.log('Draft data loaded from sheet "' + sheetName + '":', jsonData.length, 'records');
        
        if (jsonData.length === 0) {
          throw new Error('No data found in draft sheet');
        }
        
        // Log first record to understand structure
        console.log('Sample draft record:', jsonData[0]);
        console.log('Available columns:', Object.keys(jsonData[0] || {}));
        
        // Process and validate the data structure
        const processedData = jsonData.map((row, index) => {
          // Handle different possible column names from Excel
          const year = row.Year || row.year || row.YEAR;
          const owner = row.Owner || row.owner || row.OWNER || row.Manager || row.manager;
          const player = row.Player || row.player || row.PLAYER || row.Name || row.name;
          const position = row.Position || row.position || row.POSITION || row.Pos || row.pos;
          const cost = parseFloat(row.Cost || row.cost || row.COST || row.Price || row.price || 0);
          const keeperStatus = row['Keeper.Status'] || row['Keeper Status'] || row.KeeperStatus || 
                             row.keeper_status || row.Investment || row.Type || 'Drafted';
          
          // Validate required fields
          if (!year || !owner || !player || !position || isNaN(cost)) {
            console.warn('Invalid record at index ' + index + ':', row);
            return null;
          }
          
          return {
            Year: parseInt(year),
            Manager: String(owner).trim(),
            Player: String(player).trim(),
            Position: String(position).trim().toUpperCase(),
            Cost: cost,
            Investment: String(keeperStatus).trim()
          };
        }).filter(Boolean); // Remove null entries
        
        console.log('Processed data:', processedData.length, 'valid records');
        
        if (processedData.length === 0) {
          throw new Error('No valid draft records found. Please check data format.');
        }
        
        // Calculate available years and max cost from actual data
        const years = processedData.map(d => d.Year).filter(y => y && !isNaN(y));
        const costs = processedData.map(d => d.Cost).filter(c => c && !isNaN(c) && c > 0);
        
        if (years.length > 0) {
          const minYear = Math.min(...years);
          const maxYear = Math.max(...years);
          console.log('Year range:', minYear, '-', maxYear);
          setAvailableYears({ min: minYear, max: maxYear });
          setFilters(prev => ({
            ...prev,
            yearRange: [minYear, maxYear]
          }));
        }
        
        if (costs.length > 0) {
          const maxCostValue = Math.max(...costs);
          console.log('Cost range: $1 - $' + maxCostValue);
          setMaxCost(maxCostValue);
          setFilters(prev => ({
            ...prev,
            costRange: [1, maxCostValue]
          }));
        }

        // Validate managers exist in data
        const managersInData = [...new Set(processedData.map(d => d.Manager))];
        console.log('Managers found in data:', managersInData);
        
        setDraftData(processedData);
        setLoading(false);
        
      } catch (error) {
        console.error('Error parsing Excel data:', error);
        setError('Error parsing Excel data: ' + error.message);
        setLoading(false);
      }
    };

    loadDraftData();
  }, []);

  // Filter and process data for display and charts
  useEffect(() => {
    if (draftData.length > 0) {
      processChartData();
    }
  }, [filters, draftData]);

  const processChartData = () => {
    const filteredData = getFilteredData();

    // Create chart data - spending by year and investment type
    const yearInvestmentData = {};

    filteredData.forEach(record => {
      const key = record.Year + '-' + record.Investment;
      if (!yearInvestmentData[key]) {
        yearInvestmentData[key] = {
          Year: record.Year,
          Investment: record.Investment,
          Spend: 0
        };
      }
      yearInvestmentData[key].Spend += record.Cost;
    });

    // Convert to array and group by year for chart
    const yearlyData = {};
    Object.values(yearInvestmentData).forEach(item => {
      if (!yearlyData[item.Year]) {
        yearlyData[item.Year] = { Year: item.Year };
      }
      yearlyData[item.Year][item.Investment] = item.Spend;
    });

    setChartData(Object.values(yearlyData).sort((a, b) => a.Year - b.Year));
  };

  const handleManagerChange = (manager) => {
    setFilters(prev => ({
      ...prev,
      managers: prev.managers.includes(manager)
        ? prev.managers.filter(m => m !== manager)
        : [...prev.managers, manager]
    }));
  };

  const handleInvestmentTypeChange = (type) => {
    setFilters(prev => ({
      ...prev,
      investmentTypes: prev.investmentTypes.includes(type)
        ? prev.investmentTypes.filter(t => t !== type)
        : [...prev.investmentTypes, type]
    }));
  };

  const handlePositionChange = (position) => {
    setFilters(prev => ({
      ...prev,
      positions: prev.positions.includes(position)
        ? prev.positions.filter(p => p !== position)
        : [...prev.positions, position]
    }));
  };

  // Mobile Filter Component
  const MobileFilterSheet = () => (
    <div className={`fixed inset-0 z-50 lg:hidden ${showMobileFilters ? 'block' : 'hidden'}`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={() => setShowMobileFilters(false)}
      />
      
      {/* Filter Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-2 rounded-lg">
              <Search className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Draft Filters</h2>
              <p className="text-sm text-gray-600">Customize your analysis</p>
            </div>
          </div>
          <button
            onClick={() => setShowMobileFilters(false)}
            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-4 space-y-6" style={{ maxHeight: 'calc(80vh - 80px)' }}>
          {/* Year Range Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800">
                <Calendar className="h-4 w-4 text-indigo-600" />
                <span>Season Range</span>
              </label>
              <div className="bg-indigo-50 px-2 py-1 rounded-full">
                <span className="text-sm font-medium text-indigo-700">
                  {filters.yearRange[0]} - {filters.yearRange[1]}
                </span>
              </div>
            </div>
            <div className="relative">
              {/* Background track */}
              <div className="range-slider-container relative h-2 bg-gray-200 rounded-lg">
                {/* Colored range track */}
                <div 
                  className="range-slider-track absolute h-2 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg"
                  style={{
                    left: ((filters.yearRange[0] - availableYears.min) / (availableYears.max - availableYears.min)) * 100 + '%',
                    width: ((filters.yearRange[1] - filters.yearRange[0]) / (availableYears.max - availableYears.min)) * 100 + '%'
                  }}
                />
                
                {/* Start year slider */}
                <input
                  type="range"
                  min={availableYears.min}
                  max={availableYears.max}
                  value={filters.yearRange[0]}
                  onChange={(e) => {
                    const newStart = parseInt(e.target.value);
                    setFilters(prev => ({
                      ...prev,
                      yearRange: [newStart, Math.max(newStart, prev.yearRange[1])]
                    }));
                  }}
                  className="range-slider absolute w-full h-5 bg-transparent cursor-pointer appearance-none"
                  style={{ zIndex: 2, top: '-6px' }}
                />
                
                {/* End year slider */}
                <input
                  type="range"
                  min={availableYears.min}
                  max={availableYears.max}
                  value={filters.yearRange[1]}
                  onChange={(e) => {
                    const newEnd = parseInt(e.target.value);
                    setFilters(prev => ({
                      ...prev,
                      yearRange: [Math.min(prev.yearRange[0], newEnd), newEnd]
                    }));
                  }}
                  className="range-slider absolute w-full h-5 bg-transparent cursor-pointer appearance-none"
                  style={{ zIndex: 1, top: '-6px' }}
                />
              </div>
            </div>
          </div>

          {/* Managers Selection - Compact Mobile Version */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800">
                <Users className="h-4 w-4 text-indigo-600" />
                <span>Managers</span>
              </label>
              <div className="flex items-center space-x-2">
                <div className="bg-green-50 px-2 py-1 rounded-full">
                  <span className="text-sm font-medium text-green-700">
                    {filters.managers.length}/13
                  </span>
                </div>
                <button
                  onClick={() => setFilters(prev => ({
                    ...prev,
                    managers: prev.managers.length === managersOptions.length ? [] : [...managersOptions]
                  }))}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                >
                  {filters.managers.length === managersOptions.length ? 'Clear' : 'All'}
                </button>
              </div>
            </div>
            
            {/* Compact Manager Grid for Mobile */}
            <div className="grid grid-cols-3 gap-2">
              {managersOptions.map(manager => {
                const isSelected = filters.managers.includes(manager);
                return (
                  <button
                    key={manager}
                    onClick={() => handleManagerChange(manager)}
                    className={`flex items-center justify-center p-2 rounded-lg text-xs font-medium transition-all border-2 ${
                      isSelected
                        ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <span className="truncate">{manager}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Investment Type */}
          <div className="space-y-3">
            <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800">
              <Target className="h-4 w-4 text-indigo-600" />
              <span>Investment Strategy</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {investmentTypeOptions.map(type => (
                <label 
                  key={type} 
                  className={`flex items-center justify-center space-x-2 cursor-pointer p-3 rounded-lg border-2 transition-all ${
                    filters.investmentTypes.includes(type)
                      ? type === 'Drafted' 
                        ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                        : 'border-orange-300 bg-orange-50 text-orange-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={filters.investmentTypes.includes(type)}
                    onChange={() => handleInvestmentTypeChange(type)}
                    className="sr-only"
                  />
                  <span className="text-sm font-semibold">{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Positions */}
          <div className="space-y-3">
            <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800">
              <Layers className="h-4 w-4 text-indigo-600" />
              <span>Player Positions</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {positionOptions.map(position => {
                const positionColors = {
                  'QB': 'border-red-300 bg-red-50 text-red-700',
                  'RB': 'border-green-300 bg-green-50 text-green-700',
                  'WR': 'border-blue-300 bg-blue-50 text-blue-700',
                  'TE': 'border-yellow-300 bg-yellow-50 text-yellow-700',
                  'K': 'border-purple-300 bg-purple-50 text-purple-700',
                  'DEF': 'border-gray-400 bg-gray-50 text-gray-700'
                };
                
                return (
                  <label 
                    key={position} 
                    className={`flex items-center justify-center cursor-pointer p-2 rounded-lg border-2 transition-all ${
                      filters.positions.includes(position)
                        ? positionColors[position]
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={filters.positions.includes(position)}
                      onChange={() => handlePositionChange(position)}
                      className="sr-only"
                    />
                    <span className="text-xs font-bold">{position}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Cost Range */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800">
                <DollarSign className="h-4 w-4 text-indigo-600" />
                <span>Cost Range</span>
              </label>
              <div className="bg-emerald-50 px-2 py-1 rounded-full">
                <span className="text-sm font-medium text-emerald-700">
                  ${filters.costRange[0]} - ${filters.costRange[1]}
                </span>
              </div>
            </div>
            <div className="relative">
              {/* Background track */}
              <div className="range-slider-container relative h-2 bg-gray-200 rounded-lg">
                {/* Colored range track */}
                <div 
                  className="range-slider-track absolute h-2 bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg"
                  style={{
                    left: ((filters.costRange[0] - 1) / (maxCost - 1)) * 100 + '%',
                    width: ((filters.costRange[1] - filters.costRange[0]) / (maxCost - 1)) * 100 + '%'
                  }}
                />
                
                {/* Start cost slider */}
                <input
                  type="range"
                  min={1}
                  max={maxCost}
                  value={filters.costRange[0]}
                  onChange={(e) => {
                    const newStart = parseInt(e.target.value);
                    setFilters(prev => ({
                      ...prev,
                      costRange: [newStart, Math.max(newStart, prev.costRange[1])]
                    }));
                  }}
                  className="range-slider absolute w-full h-5 bg-transparent cursor-pointer appearance-none"
                  style={{ zIndex: 2, top: '-6px' }}
                />
                
                {/* End cost slider */}
                <input
                  type="range"
                  min={1}
                  max={maxCost}
                  value={filters.costRange[1]}
                  onChange={(e) => {
                    const newEnd = parseInt(e.target.value);
                    setFilters(prev => ({
                      ...prev,
                      costRange: [Math.min(prev.costRange[0], newEnd), newEnd]
                    }));
                  }}
                  className="range-slider absolute w-full h-5 bg-transparent cursor-pointer appearance-none"
                  style={{ zIndex: 1, top: '-6px' }}
                />
              </div>
            </div>
          </div>

          {/* Apply Button */}
          <div className="pt-4">
            <button
              onClick={() => setShowMobileFilters(false)}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="text-lg font-semibold text-gray-900">Loading Draft Data</span>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 max-w-md w-full mx-4">
          <div className="flex items-center space-x-3 mb-4 text-red-600">
            <X className="h-8 w-8" />
            <span className="text-lg font-semibold">Error Loading Data</span>
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Banner at the very top */}
      <div className="p-4 sm:p-6 lg:p-8">
        <DraftExplorerBanner />
      </div>
      
      {/* Mobile Filters Button */}
      {isMobile && (
        <div className="px-4 pb-4">
          <button
            onClick={() => setShowMobileFilters(true)}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold flex items-center justify-center space-x-2 hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg"
          >
            <Filter className="h-5 w-5" />
            <span>Open Filters</span>
            <div className="bg-white/20 px-2 py-1 rounded-full">
              <span className="text-sm">{filters.managers.length}</span>
            </div>
          </button>
        </div>
      )}
      
      <div className="flex flex-col lg:flex-row pb-8">
        {/* Desktop Sidebar - Hidden on Mobile */}
        {!isMobile && (
          <div className="lg:w-80 bg-white shadow-xl lg:h-fit border-r border-gray-100 lg:sticky lg:top-0">
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-3 rounded-xl shadow-lg">
                  <Search className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Draft Filters</h2>
                  <p className="text-sm text-gray-600 mt-1">Customize your analysis</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-8 max-h-screen overflow-y-auto">
              {/* Year Range Slider */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800">
                    <Calendar className="h-4 w-4 text-indigo-600" />
                    <span>Season Range</span>
                  </label>
                  <div className="bg-indigo-50 px-3 py-1 rounded-full">
                    <span className="text-sm font-medium text-indigo-700">
                      {filters.yearRange[0]} - {filters.yearRange[1]}
                    </span>
                  </div>
                </div>
                <div className="relative">
                  {/* Background track */}
                  <div className="range-slider-container relative h-2 bg-gray-200 rounded-lg">
                    {/* Colored range track */}
                    <div 
                      className="range-slider-track absolute h-2 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg"
                      style={{
                        left: ((filters.yearRange[0] - availableYears.min) / (availableYears.max - availableYears.min)) * 100 + '%',
                        width: ((filters.yearRange[1] - filters.yearRange[0]) / (availableYears.max - availableYears.min)) * 100 + '%'
                      }}
                    />
                    
                    {/* Start year slider */}
                    <input
                      type="range"
                      min={availableYears.min}
                      max={availableYears.max}
                      value={filters.yearRange[0]}
                      onChange={(e) => {
                        const newStart = parseInt(e.target.value);
                        setFilters(prev => ({
                          ...prev,
                          yearRange: [newStart, Math.max(newStart, prev.yearRange[1])]
                        }));
                      }}
                      className="range-slider absolute w-full h-5 bg-transparent cursor-pointer appearance-none"
                      style={{ zIndex: 2, top: '-6px' }}
                    />
                    
                    {/* End year slider */}
                    <input
                      type="range"
                      min={availableYears.min}
                      max={availableYears.max}
                      value={filters.yearRange[1]}
                      onChange={(e) => {
                        const newEnd = parseInt(e.target.value);
                        setFilters(prev => ({
                          ...prev,
                          yearRange: [Math.min(prev.yearRange[0], newEnd), newEnd]
                        }));
                      }}
                      className="range-slider absolute w-full h-5 bg-transparent cursor-pointer appearance-none"
                      style={{ zIndex: 1, top: '-6px' }}
                    />
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>{availableYears.min}</span>
                    <span>{availableYears.max}</span>
                  </div>
                </div>
              </div>

              {/* Managers Selection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800">
                    <Users className="h-4 w-4 text-indigo-600" />
                    <span>Managers</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className="bg-green-50 px-3 py-1 rounded-full">
                      <span className="text-sm font-medium text-green-700">
                        {filters.managers.length}/13
                      </span>
                    </div>
                    <button
                      onClick={() => setFilters(prev => ({
                        ...prev,
                        managers: prev.managers.length === managersOptions.length ? [] : [...managersOptions]
                      }))}
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                    >
                      {filters.managers.length === managersOptions.length ? 'Clear All' : 'Select All'}
                    </button>
                  </div>
                </div>
                
                {/* Quick Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFilters(prev => ({
                      ...prev,
                      managers: [...managersOptions]
                    }))}
                    className={'px-3 py-1.5 rounded-lg text-xs font-medium transition-all ' + 
                      (filters.managers.length === managersOptions.length
                        ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                    )}
                  >
                    ✓ All
                  </button>
                  <button
                    onClick={() => setFilters(prev => ({
                      ...prev,
                      managers: []
                    }))}
                    className={'px-3 py-1.5 rounded-lg text-xs font-medium transition-all ' + 
                      (filters.managers.length === 0
                        ? 'bg-red-100 text-red-700 border border-red-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                    )}
                  >
                    ✗ None
                  </button>
                </div>

                {/* Manager Grid */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="grid grid-cols-2 gap-2">
                    {managersOptions.map(manager => {
                      const isSelected = filters.managers.includes(manager);
                      const imageUrl = `/${manager.toLowerCase()}main.png`;
                      return (
                        <button
                          key={manager}
                          onClick={() => handleManagerChange(manager)}
                          className={'group flex items-center justify-center p-3 rounded-xl text-sm font-medium transition-all duration-300 relative overflow-hidden border-2 ' + 
                            (isSelected
                              ? 'border-indigo-400 shadow-lg transform scale-105'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                          )}
                          style={{
                            backgroundImage: `url(${imageUrl})`,
                            backgroundSize: '120%',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            filter: isSelected ? 'none' : 'grayscale(100%)',
                          }}
                        >
                          {/* Subtle background overlay for text contrast */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                          
                          <div className="flex items-center space-x-2 relative z-10">
                            <div className={`w-3 h-3 rounded-full transition-all duration-300 border-2 ${
                              isSelected 
                                ? 'bg-yellow-400 border-yellow-300 shadow-lg shadow-yellow-400/50 animate-pulse' 
                                : 'bg-gray-400 border-gray-300'
                            }`}></div>
                            <span className={`truncate font-bold text-shadow transition-colors duration-300 ${
                              isSelected ? 'text-white' : 'text-gray-800'
                            }`} style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}>
                              {manager}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Selected Summary */}
                {filters.managers.length > 0 && filters.managers.length < managersOptions.length && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <div className="text-xs font-medium text-blue-800 mb-2">Selected Managers:</div>
                    <div className="flex flex-wrap gap-1">
                      {filters.managers.map(manager => (
                        <span
                          key={manager}
                          className="inline-flex items-center bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded-full"
                        >
                          {manager}
                          <button
                            onClick={() => handleManagerChange(manager)}
                            className="ml-1 hover:text-blue-900 transition-colors"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Investment Type */}
              <div className="space-y-4">
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800">
                  <Target className="h-4 w-4 text-indigo-600" />
                  <span>Investment Strategy</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {investmentTypeOptions.map(type => (
                    <label 
                      key={type} 
                      className={'group flex items-center justify-center space-x-2 cursor-pointer p-3 rounded-xl border-2 transition-all ' + 
                        (filters.investmentTypes.includes(type)
                          ? type === 'Drafted' 
                            ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                            : 'border-orange-300 bg-orange-50 text-orange-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-700'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={filters.investmentTypes.includes(type)}
                        onChange={() => handleInvestmentTypeChange(type)}
                        className="sr-only"
                      />
                      <span className="text-sm font-semibold">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Positions */}
              <div className="space-y-4">
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800">
                  <Layers className="h-4 w-4 text-indigo-600" />
                  <span>Player Positions</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {positionOptions.map(position => {
                    const positionColors = {
                      'QB': 'border-red-300 bg-red-50 text-red-700',
                      'RB': 'border-green-300 bg-green-50 text-green-700',
                      'WR': 'border-blue-300 bg-blue-50 text-blue-700',
                      'TE': 'border-yellow-300 bg-yellow-50 text-yellow-700',
                      'K': 'border-purple-300 bg-purple-50 text-purple-700',
                      'DEF': 'border-gray-400 bg-gray-50 text-gray-700'
                    };
                    
                    return (
                      <label 
                        key={position} 
                        className={'group flex items-center justify-center cursor-pointer p-2 rounded-lg border-2 transition-all ' + 
                          (filters.positions.includes(position)
                            ? positionColors[position]
                            : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-700'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={filters.positions.includes(position)}
                          onChange={() => handlePositionChange(position)}
                          className="sr-only"
                        />
                        <span className="text-xs font-bold">{position}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Cost Range */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800">
                    <DollarSign className="h-4 w-4 text-indigo-600" />
                    <span>Cost Range</span>
                  </label>
                  <div className="bg-emerald-50 px-3 py-1 rounded-full">
                    <span className="text-sm font-medium text-emerald-700">
                      ${filters.costRange[0]} - ${filters.costRange[1]}
                    </span>
                  </div>
                </div>
                <div className="relative">
                  {/* Background track */}
                  <div className="range-slider-container relative h-2 bg-gray-200 rounded-lg">
                    {/* Colored range track */}
                    <div 
                      className="range-slider-track absolute h-2 bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg"
                      style={{
                        left: ((filters.costRange[0] - 1) / (maxCost - 1)) * 100 + '%',
                        width: ((filters.costRange[1] - filters.costRange[0]) / (maxCost - 1)) * 100 + '%'
                      }}
                    />
                    
                    {/* Start cost slider */}
                    <input
                      type="range"
                      min={1}
                      max={maxCost}
                      value={filters.costRange[0]}
                      onChange={(e) => {
                        const newStart = parseInt(e.target.value);
                        setFilters(prev => ({
                          ...prev,
                          costRange: [newStart, Math.max(newStart, prev.costRange[1])]
                        }));
                      }}
                      className="range-slider absolute w-full h-5 bg-transparent cursor-pointer appearance-none"
                      style={{ zIndex: 2, top: '-6px' }}
                    />
                    
                    {/* End cost slider */}
                    <input
                      type="range"
                      min={1}
                      max={maxCost}
                      value={filters.costRange[1]}
                      onChange={(e) => {
                        const newEnd = parseInt(e.target.value);
                        setFilters(prev => ({
                          ...prev,
                          costRange: [Math.min(prev.costRange[0], newEnd), newEnd]
                        }));
                      }}
                      className="range-slider absolute w-full h-5 bg-transparent cursor-pointer appearance-none"
                      style={{ zIndex: 1, top: '-6px' }}
                    />
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>$1</span>
                    <span>${maxCost}</span>
                  </div>
                </div>
              </div>

              {/* Filter Summary */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                  <Eye className="h-4 w-4 text-indigo-600 mr-2" />
                  Active Filters
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Seasons:</span>
                    <span className="font-semibold text-gray-800">{filters.yearRange[1] - filters.yearRange[0] + 1} years</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Managers:</span>
                    <span className="font-semibold text-gray-800">{filters.managers.length}/13</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Positions:</span>
                    <span className="font-semibold text-gray-800">{filters.positions.length}/6</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Investment Types:</span>
                    <span className="font-semibold text-gray-800">{filters.investmentTypes.length}/2</span>
                  </div>
                </div>
              </div>

              {/* Logo placeholder */}
              <div className="pt-4 flex justify-center">
                <div className="w-40 h-40 bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 rounded-2xl flex items-center justify-center shadow-lg border border-indigo-200">
                  <Trophy className="h-12 w-12 text-indigo-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8 min-h-0">
          {/* Data Table */}
          <DraftTable 
            data={filteredData}
            loading={loading}
            error={error}
          />

          {/* Investment Chart */}
          <InvestmentChart 
            data={chartData}
            rawData={filteredData}
            loading={loading}
            error={error}
          />
        </div>
      </div>

      {/* Mobile Filter Sheet */}
      <MobileFilterSheet />

      <style jsx>{`
        .range-slider-container {
          position: relative;
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          margin: 10px 0;
        }
        
        .range-slider-track {
          position: absolute;
          height: 8px;
          border-radius: 4px;
          top: 0;
        }
        
        .range-slider {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
          outline: none;
        }
        
        .range-slider::-webkit-slider-track {
          background: transparent;
          height: 20px;
        }
        
        .range-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          border: 3px solid white;
          transition: all 0.2s ease;
        }
        
        .range-slider::-webkit-slider-thumb:hover {
          background: #7c3aed;
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        
        .range-slider::-moz-range-track {
          background: transparent;
          height: 20px;
          border: none;
        }
        
        .range-slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          transition: all 0.2s ease;
        }
        
        .range-slider::-moz-range-thumb:hover {
          background: #7c3aed;
          transform: scale(1.1);
        }
        
        .range-slider:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.3);
        }
        
        .range-slider:focus::-moz-range-thumb {
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.3);
        }
      `}</style>
    </div>
  );
};

export default DraftExplorer;